import { type I18n } from '@lingui/core';
import { t } from '@lingui/macro';
import dayjs from 'dayjs';
import { z } from 'zod/v4';

/**
 * Espejo de `DGA_TICKET_CODE_PATTERN` y `normalizeDgaTicketCode` del backend
 * (`services/dga/utils.ts`, AMARU-718). Se duplican a propósito: el backend protege el dato aunque
 * la mutación se llame por fuera de la UI, esto le avisa al usuario antes de mandarla. Si cambia un
 * lado, cambia el otro.
 */
export const TICKET_CODE_PATTERN = /^\d{4,12}$/;

/**
 * La plataforma de la DGA muestra el código con `#`, así que copiar y pegar trae `#721844` o
 * espacios. Se limpia en vez de rechazar.
 *
 * No intenta rescatar un código embebido en prosa: `"Ticket de soporte abierto [#721844]"` sigue
 * siendo inválido a propósito. Elegir cuál de varios números es el código sería adivinar, y ese
 * valor termina impreso en el PDF que recibe el cliente (AMARU-733).
 */
export const normalizeTicketCode = (raw: string): string => raw.trim().replace(/^#+\s*/, '');

/**
 * Largo máximo del detalle. Espejo de `DGA_TICKET_DESCRIPTION_MAX_LENGTH` del backend
 * (`services/dga/utils.ts`, AMARU-736), con el mismo criterio que el patrón del código: el backend
 * es la autoridad, esto le avisa al usuario antes de mandar. Si cambia un lado, cambia el otro.
 */
export const TICKET_DESCRIPTION_MAX_LENGTH = 1000;

/**
 * Formulario de apertura. Los campos se llaman como el wire (`code`, `openedAt`) para que no haga
 * falta una capa de mapeo entre el form y las variables de la mutación.
 */
export const ValidationSchema = (i18n: I18n) =>
  z.object({
    deviceId: z.string(),
    // El `transform` corre antes del `refine`, así que se valida —y se envía— el código ya limpio.
    // El campo conserva lo que el usuario tipeó; lo que se normaliza es el valor que sale del form.
    code: z
      .string()
      .min(1, t(i18n)`Este campo no puede estar vacío`)
      .transform(normalizeTicketCode)
      .refine(
        (code) => TICKET_CODE_PATTERN.test(code),
        t(i18n)`Solo números, entre 4 y 12 dígitos, sin texto ni fechas`,
      ),
    openedAt: z.custom<dayjs.Dayjs>((val) => val instanceof dayjs, t(i18n)`Fecha inválida`),
    // Texto libre opcional: es contexto operativo interno, no viaja al PDF del cliente. El largo se
    // mide después del `trim`, igual que el backend — espacios de sobra no son motivo de rechazo.
    description: z
      .string()
      .trim()
      .max(
        TICKET_DESCRIPTION_MAX_LENGTH,
        t(i18n)`Máximo ${TICKET_DESCRIPTION_MAX_LENGTH} caracteres`,
      )
      .optional(),
  });

/**
 * Formulario de corrección del ticket abierto: mismos campos, todos editables, sin `deviceId` —
 * el device ya está fijado por el ticket que se está corrigiendo.
 *
 * Se deriva con `.pick()` a propósito: arrastra el `transform` + `refine` de `code`, y ese es
 * justamente el camino que limpia los códigos viejos escritos con prosa. Si acá no validara, la
 * corrección no serviría para lo que se pensó.
 */
export const EditValidationSchema = (i18n: I18n) =>
  ValidationSchema(i18n).pick({ code: true, openedAt: true, description: true });

export type FormValues = z.output<ReturnType<typeof ValidationSchema>>;

export type EditFormValues = z.output<ReturnType<typeof EditValidationSchema>>;
